import { uniq } from 'es-toolkit';
import React, { useEffect, useMemo, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, IconButton, Toolbar, Typography } from '@mui/material';

import { formatLengthToFeetInchesSixteenthsString } from '@paragon/client-tools';
import { EstimateInfo, EstimateMetricsAndMissingPrices, ProjectLayoutModel } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { CreateEstimate, getDefaultEstimateInfo } from '@paragon/estimate-tools';
import {
  getComponentDesignQuantities,
  getFlattenedChildrenWithCalculatedQuantity,
  getLayoutModelsWithPendingChanges,
} from '@paragon/layout-models';
import { getLoggedInUser } from '@paragon/login-tools';
import { OptionalTooltip } from '@paragon/material';
import { lumbersAreEqual } from '@paragon/material-lists';

import { createEstimateCsv, createEstimatePdf } from '../../store/actions/download';
import { CountFileUploader } from '../dialogs/CountFileUploader';
import { useAppDispatch, useAppSelector } from '../hooks';
import type { EstimatesState } from './index';

interface Props {
  readonly project: ProjectLayoutModel;
  readonly existingProjectEstimates: EstimatesState;

  readonly createDisabledReason: string | null;

  readonly onClose: () => void;
  readonly onCreated: () => void;
}

/**
 * The create-estimate workflow embedded in the drawer. The drawer hides its chrome and goes fullscreen
 * while this is open; the wizard follows the app's light/dark theme.
 */
export function EstimateWizard({ project, existingProjectEstimates, createDisabledReason, onClose, onCreated }: Props) {
  const dispatch = useAppDispatch();

  const projectGuid = project.guid;
  const layoutModelsByGuid = useAppSelector(getLayoutModelsWithPendingChanges);
  const flattenedChildrenWithCalculatedQuantityGetter = useAppSelector(getFlattenedChildrenWithCalculatedQuantity);
  const loggedInUser = useAppSelector(getLoggedInUser);
  const componentDesignQuantities = useMemo(
    () => getComponentDesignQuantities(flattenedChildrenWithCalculatedQuantityGetter(projectGuid)),
    [flattenedChildrenWithCalculatedQuantityGetter, projectGuid],
  );

  const [estimateInfo, setEstimateInfo] = useState<EstimateInfo>(() =>
    getDefaultEstimateInfo(project.name, loggedInUser!.email, componentDesignQuantities),
  );
  const [requireNonZeroPrices, setRequireNonZeroPrices] = useState<boolean>(false);
  const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [estimateMetricsAndMissingPrices, setEstimateMetricsAndMissingPrices] = useState<
    EstimateMetricsAndMissingPrices | 'loading' | 'error'
  >('loading');

  useEffect(() => {
    designService.estimates
      .getEstimateMetricsAndMissingPricesForProject(projectGuid)
      .then((estimateMetricsAndMissingPricesFromServer) => {
        setRequireNonZeroPrices(true);
        setEstimateMetricsAndMissingPrices(estimateMetricsAndMissingPricesFromServer);
        setEstimateInfo((oldEstimateInfo) => ({
          ...oldEstimateInfo,
          userSpecifiedLumberPrices: estimateMetricsAndMissingPricesFromServer.missingLumberPrices.map(
            (missingLumberPrice) => ({ ...missingLumberPrice, price: 0 }),
          ),
          userSpecifiedPlatePrices: Object.fromEntries(
            uniq(estimateMetricsAndMissingPricesFromServer.missingPlateTypePrices).map((plateType) => [plateType, 0]),
          ),
          userSpecifiedHangerPrices: Object.fromEntries(
            uniq(estimateMetricsAndMissingPricesFromServer.missingHangerPrices).map((hanger) => [hanger, 0]),
          ),
          userSpecifiedCustomHangerPrices: Object.fromEntries(
            uniq(estimateMetricsAndMissingPricesFromServer.missingCustomHangerPrices).map((hanger) => [hanger, 0]),
          ),
        }));
      })
      .catch((error) => {
        console.error(error);
        setEstimateMetricsAndMissingPrices('error');
        setRequireNonZeroPrices(false);
      });
    return () => setEstimateMetricsAndMissingPrices('loading');
  }, [projectGuid]);

  const allNecessaryPricesSet = useMemo(() => {
    const anyPlatePriceMissing = Object.values(estimateInfo.userSpecifiedPlatePrices).some((price) => price === 0);
    if (anyPlatePriceMissing) return false;

    const anyHangerPriceMissing = estimateInfo.includeHangerPricing
      ? Object.values(estimateInfo.userSpecifiedHangerPrices).some((price) => price === 0) ||
        Object.values(estimateInfo.userSpecifiedCustomHangerPrices).some((price) => price === 0)
      : false;
    if (anyHangerPriceMissing) return false;

    for (const userSpecifiedLumberPrice of estimateInfo.userSpecifiedLumberPrices) {
      if (userSpecifiedLumberPrice.price !== 0) continue;
      if (
        !estimateInfo.userSpecifiedLumberPrices.some(
          (otherLumberPrice) =>
            lumbersAreEqual(otherLumberPrice.lumber, userSpecifiedLumberPrice.lumber) &&
            otherLumberPrice.stockLengthInInches > userSpecifiedLumberPrice.stockLengthInInches,
        )
      )
        return false;
    }

    return true;
  }, [estimateInfo]);

  const additionalQuantityControls: [
    string,
    React.ComponentType<{
      quantities: { [guid: string]: number };
      onQuantityChange: (componentDesignQuantitiesWithModels: { [guid: string]: number }) => void;
    }>,
  ][] = [['countFileUploader', CountFileUploader]];

  // Until the missing prices load, requireNonZeroPrices is still false, so generating would skip price validation.
  const generateDisabledReason =
    createDisabledReason ?? (estimateMetricsAndMissingPrices === 'loading' ? 'Loading prices...' : null);

  const generate = async (create: typeof createEstimatePdf) => {
    if (requireNonZeroPrices && !allNecessaryPricesSet) {
      setSubmitAttempted(true);
      return;
    }

    setGenerating(true);
    try {
      if (await dispatch(create(estimateInfo, projectGuid))) onCreated();
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.default' }}>
      <Toolbar variant="dense" sx={{ borderBottom: 1, borderColor: 'divider', gap: 1 }}>
        <IconButton edge="start" disabled={generating} onClick={onClose} aria-label="Back to estimates">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Create Estimate
        </Typography>
        <OptionalTooltip title={generateDisabledReason} visible={generateDisabledReason != null}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              disabled={generating || generateDisabledReason != null}
              onClick={() => generate(createEstimateCsv)}
            >
              Generate CSV
            </Button>
            <Button
              variant="contained"
              disabled={generating || generateDisabledReason != null}
              onClick={() => generate(createEstimatePdf)}
            >
              Generate PDF
            </Button>
          </Box>
        </OptionalTooltip>
      </Toolbar>

      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        <CreateEstimate
          estimateInfo={estimateInfo}
          setEstimateInfo={setEstimateInfo}
          existingProjectEstimates={existingProjectEstimates}
          estimateMetricsAndMissingPrices={estimateMetricsAndMissingPrices}
          requireNonZeroPrices={requireNonZeroPrices}
          setRequireNonZeroPrices={setRequireNonZeroPrices}
          distanceFormatter={formatLengthToFeetInchesSixteenthsString}
          componentDesignQuantities={componentDesignQuantities}
          layoutModelsByGuid={layoutModelsByGuid}
          additionalQuantityControls={additionalQuantityControls}
          submitAttemptedWithoutAllPrices={submitAttempted && !allNecessaryPricesSet}
        />
      </Box>
    </Box>
  );
}
